import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  signal,
  SimpleChanges
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { Icons } from '../../../shared/helpers/icons';
import {
  Agent,
  AgentFormState,
  AgentSavePayload
} from '../../../shared/models/agent';

export type TrackerModalMode = 'create' | 'edit';

const EMPTY_AGENT_FORM: AgentFormState = {
  id: '',
  name: '',
  description: '',
  type: 'Tracker',
  hardwareType: '',
  hardwareName: '',
  hardwareModelNumber: '',
  hardwareSerialNumber: '',
  phoneNumber: '',
  imei: '',
  iccid: '',
  osName: '',
  osVersion: '',
  appName: '',
  appVersion: '',
  timeoutOnline: 7200,
  timeoutActive: 300,
  fleetVersion: null,
  isActive: true
};

@Component({
  selector: 'tracker-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule],
  templateUrl: './tracker-modal.component.html'
})
export class TrackerModalComponent implements OnChanges {
  icons = Icons;
  readonly agentTypes: readonly string[] = ['Tracker'];

  @Input() mode: TrackerModalMode = 'create';
  @Input() open = false;
  @Input() saving = false;
  /** Only consulted in edit mode. */
  @Input() initialAgent: Agent | null = null;

  @Output() save = new EventEmitter<AgentSavePayload>();
  @Output() closeModal = new EventEmitter<void>();

  form = signal<AgentFormState>({ ...EMPTY_AGENT_FORM });
  /** Fields the user has interacted with (blurred at least once). We only
   *  show validation errors for touched fields so they don't flash on open. */
  touched = signal<Set<string>>(new Set<string>());

  // v2 keeps the device-id read-only on edit (it's the row PK) and the
  // hardware block read-only when the agent type is "Mobile" (the device
  // pushes those values itself; the admin shouldn't override them).
  //
  // For WLI trackers (fleetVersion 1 or 2) the id is derived from the
  // serial as `WLI-<serial>` per convention (see fleet-api/PROVISIONING.md),
  // so the Unit ID input is also locked in create mode and mirrors the
  // serial the user types.
  readonly isWliTracker = computed(
    () => this.form().fleetVersion === 1 || this.form().fleetVersion === 2
  );
  readonly idLocked = computed(
    () => this.mode === 'edit' || (this.mode === 'create' && this.isWliTracker())
  );
  readonly isMobileAgent = computed(() => this.form().type === 'Mobile');
  readonly hardwareLocked = computed(
    () => this.mode === 'edit' && this.isMobileAgent()
  );

  /** Serial must be 6-7 digits when we're deriving the WLI id from it —
   *  matches the actual WLI hardware serial format. Enforced only in the
   *  create + WLI path; edit and non-WLI paths accept anything. Pastes of
   *  the full "WLI-<serial>" form are stripped in `updateField` before
   *  this check runs, so operators can copy-paste either form. */
  private static readonly WLI_SERIAL_RE = /^\d{6,7}$/;
  readonly serialFormatInvalid = computed<boolean>(() => {
    if (this.mode !== 'create' || !this.isWliTracker()) return false;
    const s = this.form().hardwareSerialNumber.trim();
    return !!s && !TrackerModalComponent.WLI_SERIAL_RE.test(s);
  });

  /** False when a required field is missing — drives the submit-button
   *  disabled state. Unit ID is locked + always populated in edit mode.
   *  Serial Number is exempt while the hardware block is locked (Mobile
   *  agents push it themselves and may legitimately have none). */
  readonly canSubmit = computed<boolean>(() => {
    const f = this.form();
    return (
      !!f.id.trim() &&
      !!f.name.trim() &&
      (this.hardwareLocked() || !!f.hardwareSerialNumber.trim()) &&
      !this.serialFormatInvalid()
    );
  });

  markTouched(field: string): void {
    this.touched.update(s => {
      if (s.has(field)) return s;
      const next = new Set(s);
      next.add(field);
      return next;
    });
  }

  showError(field: string, invalid: boolean): boolean {
    return invalid && this.touched().has(field);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Re-hydrate the form whenever the modal opens or the bound agent
    // changes. In create mode we use the blank defaults; in edit mode we
    // copy from `initialAgent`.
    if (changes['open'] || changes['initialAgent'] || changes['mode']) {
      if (!this.open) {
        this.touched.set(new Set<string>());
        return;
      }
      // Fresh open = clean slate; no fields touched yet.
      this.touched.set(new Set<string>());
      if (this.mode === 'edit' && this.initialAgent) {
        const a = this.initialAgent;
        this.form.set({
          id: a.id,
          name: a.name ?? '',
          description: a.description ?? '',
          type: a.type ?? 'Tracker',
          hardwareType: a.hardwareType ?? '',
          hardwareName: a.hardwareName ?? '',
          hardwareModelNumber: a.hardwareModelNumber ?? '',
          hardwareSerialNumber: a.hardwareSerialNumber ?? '',
          phoneNumber: a.phoneNumber ?? '',
          imei: a.imei ?? '',
          iccid: a.iccid ?? '',
          osName: a.osName ?? '',
          osVersion: a.osVersion ?? '',
          appName: a.appName ?? '',
          appVersion: a.appVersion ?? '',
          timeoutOnline: a.timeoutOnline ?? 7200,
          timeoutActive: a.timeoutActive ?? 300,
          fleetVersion: a.fleetVersion ?? null,
          // Legacy rows with NULL default to active — matches the read-side
          // filter behaviour (rows only render when isActive=1) so operators
          // don't see a false "inactive" state for something the app shows.
          isActive: a.isActive !== 0
        });
      } else if (this.mode === 'create') {
        this.form.set({ ...EMPTY_AGENT_FORM });
      }
    }
  }

  updateField<K extends keyof AgentFormState>(
    key: K,
    value: AgentFormState[K]
  ): void {
    this.form.update(f => {
      const next = { ...f, [key]: value };
      // Mirror serial into id as `WLI-<serial>` while the id is being
      // derived (WLI tracker + create mode). Runs on serial edits and on
      // fleetVersion changes so switching to fleet-api immediately picks
      // up whatever serial is already typed. Operators can paste either
      // "862921" or "WLI-862921" — the prefix is stripped before the
      // 6-7 digit validation runs.
      if (this.mode === 'create' && (key === 'hardwareSerialNumber' || key === 'fleetVersion')) {
        const derivingId =
          next.fleetVersion === 1 || next.fleetVersion === 2;
        if (derivingId) {
          const raw = next.hardwareSerialNumber.trim();
          const stripped = raw.replace(/^WLI-/i, '');
          if (stripped !== next.hardwareSerialNumber) {
            next.hardwareSerialNumber = stripped;
          }
          next.id = stripped ? `WLI-${stripped}` : '';
        }
      }
      return next;
    });
  }

  onSubmit(): void {
    const f = this.form();
    if (!this.canSubmit()) return;
    this.save.emit({
      // Editing PUTs by the original id; creating uses what the user typed.
      id: this.mode === 'edit' ? f.id : f.id.trim(),
      name: f.name.trim(),
      description: f.description || null,
      type: f.type || null,
      hardwareType: f.hardwareType || null,
      hardwareName: f.hardwareName || null,
      hardwareModelNumber: f.hardwareModelNumber || null,
      hardwareSerialNumber: f.hardwareSerialNumber || null,
      phoneNumber: f.phoneNumber || null,
      imei: f.imei || null,
      iccid: f.iccid || null,
      osName: f.osName || null,
      osVersion: f.osVersion || null,
      appName: f.appName || null,
      appVersion: f.appVersion || null,
      timeoutOnline: f.timeoutOnline,
      timeoutActive: f.timeoutActive,
      fleetVersion: f.fleetVersion,
      isActive: f.isActive ? 1 : 0
    });
  }

  onClose(): void {
    if (this.saving) return;
    this.closeModal.emit();
  }
}
